
import type { SynonymTypeConfig } from '@empathybroker/tsclient-play/dist/models/instance/domains/tools/synonym-type-config'
import type { SynonymsConfig } from '@empathybroker/tsclient-play/dist/models/instance/domains/tools/synonyms-config'
import type { ConflictingElementInfo } from '@empathyco/playboard-core-domain'
import type { Action } from '../../components/shared/epb-pill'
import type { Dictionary, RegularFilter } from '../../models'
import type { StatusModel } from '../../services/interfaces/semantic.service'
import type { SemanticService } from '../../services/semantic.service'
import { ActionTypeEnum } from '@empathybroker/tsclient-play/dist/enums/action-type.enum'
import { PlayErrorReasonEnum } from '@empathybroker/tsclient-play/dist/enums/play-error-reason.enum'
import { ConflictApiError } from '@empathyco/playboard-core-domain'
import { defineComponent } from 'vue'
import draggable from 'vuedraggable'
import { mapGetters } from 'vuex'
import ConflictErrorModalWithOptions from '../../components/crud/conflict-error-modal/conflict-error-modal-with-options.component.vue'
import { UiProgressLinear, UiRadioGroup } from '../../components/keen-ui'
import { EpbBadge } from '../../components/shared/epb-badge'
import { EpbPill } from '../../components/shared/epb-pill'
import { EpbTextbox } from '../../components/shared/epb-textbox'
import ToolingFormLayout from '../../components/tooling/tooling-form-layout.component.vue'
import { CONTAINER } from '../../config/inversify.config'
import { TYPES } from '../../config/inversify.types'
import { DeviceEnum, FeatureIdEnum, FilterIdEnum, KeyboardCodeEnum } from '../../enums'
import { CrudFormMixin } from '../../mixins/crud-form.mixin'
import { ROUTER_CONSTS } from '../../router/router.consts'
import { CONFIG_CONSTS } from '../../store/config/config.consts'
import { ArrayUtils } from '../../utils/array.utils'

export enum SynonymsTypes {
  SYNONYMS = 'synonyms',
  HYPONYMS = 'hyponyms',
}

export default defineComponent({
  name: 'SynonymsForm',
  components: {
    Draggable: draggable,
    EpbBadge,
    EpbTextbox,
    EpbPill,
    ConflictErrorModalWithOptions,
    ToolingFormLayout,
    UiProgressLinear,
    UiRadioGroup,
  },
  mixins: [CrudFormMixin],
  computed: {
    ...mapGetters(CONFIG_CONSTS.moduleName, {
      currentInstance: CONFIG_CONSTS.getters.currentInstance,
    }),
    toolConfig(): SynonymsConfig {
      const toolConfig = CrudFormMixin.computed.toolConfig.bind(this)()
      if (!toolConfig.types?.length) {
        return {
          ...toolConfig,
          types: [
            {
              id: 'basic',
            },
          ],
        }
      }
      return toolConfig
    },
    formIsValid(): boolean {
      if (!this.item.type) {
        return false
      }
      if (!this.item.filters.lang) {
        return false
      }
      if (
        !this.item.synonyms.length ||
        (!this.item.hyponyms.length && this.item.synonyms.length < 2)
      ) {
        return false
      }
      return true
    },
    typeRadioGroupOptions(): any[] {
      const typeValuesMessages = this.messages.fields.type.values
      return this.toolConfig.types.map((type: SynonymTypeConfig) => ({
        value: type.id,
        label: type.prettyName || (!!typeValuesMessages && typeValuesMessages[type.id]) || type.id,
      }))
    },
    typeSelectedValue(): string {
      const selectedOption = this.typeRadioGroupOptions.find(
        (option: any) => option.value === this.item.type,
      )
      return selectedOption ? selectedOption.value : ''
    },
    isTypeVisible(): boolean {
      return this.toolConfig.types.length > 1
    },
    filters(): Dictionary<RegularFilter> {
      const filters = CrudFormMixin.computed.filters.bind(this)()
      filters.lang.config = {
        ...filters.lang.config,
        mandatory: true,
        multiselectDisabled: true,
      }
      return filters
    },
    isLangVisible(): boolean {
      return this.context.filters[FilterIdEnum.LANG].config.values.length > 1
    },
    tagAction(): Action[] {
      return [{ id: 'discard', icon: 'close' }]
    },
    saveConflictApiError(): ConflictApiError {
      return new ConflictApiError({
        conflictingElements: this.conflictingElements?.length
          ? this.conflictingElements.map(conflictingElement => {
              return {
                id: conflictingElement.id,
                queries: conflictingElement.synonyms,
              } as ConflictingElementInfo
            })
          : [],
      })
    },
  },
  data() {
    return {
      PlayErrorReasonEnum,
      baseClass: 'synonyms-form',
      dataType: ActionTypeEnum.SYNONYM,
      updateMode: this.$route.name === ROUTER_CONSTS.routes[FeatureIdEnum.SYNONYM].update,
      suggestionsStatusByInstance: [],
      suggestions: {
        from: '',
        data: [],
      },
      synonyms: [],
      hyponyms: [],
      item: {
        enabled: true,
        type: undefined,
        synonyms: [],
        hyponyms: [],
        filters: {
          lang: undefined,
        },
      },
      DeviceEnum,
      SynonymsTypes,
      inputs: {
        synonyms: '',
        hyponyms: '',
      },
      messages: this.$epb.messages.tools.synonyms,
      help: {
        synonyms: this.$epb.messages.tools.synonyms.fields.synonyms.help,
        hyponyms: this.$epb.messages.tools.synonyms.fields.hyponyms.help,
      },
      selectedTabId: SynonymsTypes.SYNONYMS,
      conflictingSynonym: undefined,
      loadingSuggestions: false,
      synonymsUpdateRoute: ROUTER_CONSTS.routes[FeatureIdEnum.SYNONYM].update,
    }
  },
  watch: {
    conflictingElements() {
      this.$refs.saveConflictErrorModal.open()
    },
  },
  async mounted(): Promise<void> {
    const semanticService = CONTAINER.get<SemanticService>(TYPES.SemanticService)

    if (semanticService) {
      try {
        const instanceWithSuggestions = await semanticService.checkStatus()
        this.suggestionsStatusByInstance = instanceWithSuggestions.filter(
          (model: StatusModel) => model.instance === this.currentInstance.id,
        )
      } catch (e) {
        this.suggestionsStatusByInstance = []
      }
    }
  },
  methods: {
    onItemLoad(): void {
      CrudFormMixin.methods.onItemLoad.bind(this)()
      if (!this.item.type) {
        this.item.type = this.toolConfig.types[0].id
      }

      this.synonyms = this.item.synonyms.map(query => ({ query, comesFromInput: true }))
      this.hyponyms = this.item.hyponyms.map(query => ({ query, comesFromInput: true }))
    },
    onInputKeydown(keyboardEvent: KeyboardEvent, type: SynonymsTypes) {
      if (keyboardEvent.key === KeyboardCodeEnum.ENTER || keyboardEvent.key === ';') {
        this.addValues(type)
        keyboardEvent.preventDefault()
      }
    },
    addValues(type: SynonymsTypes) {
      const values: string[] = this.inputs[type].toLowerCase().split(';')
      const firstPosition = 0
      if (!values[firstPosition]) {
        return
      }

      values.forEach((value: string, index) => {
        const normalizedValue = value.trim()
        if (
          !this.item.synonyms.includes(normalizedValue) &&
          !this.item.hyponyms.includes(normalizedValue)
        ) {
          this[type].push({
            selected: index === firstPosition,
            comesFromInput: true,
            query: normalizedValue,
          })

          this.item[type].push(normalizedValue)

          this.inputs[type] = ''
        }
      })

      this.clearSelectedValues(values[firstPosition])
      this.getSuggestions(values[firstPosition])
    },
    clearSelectedValues(query: string) {
      this.synonyms.forEach(synonym => {
        synonym.selected = synonym.query === query
      })

      this.hyponyms.forEach(hyponym => {
        hyponym.selected = hyponym.query === query
      })
    },
    async getSuggestions(query: string) {
      this.loadingSuggestions = true
      const lang = this.filters.lang.selectedValues[0]
      const suggestionStatus: StatusModel = this.suggestionsStatusByInstance.find(
        (suggestionsStatus: StatusModel) => suggestionsStatus.lang === lang,
      )

      this.suggestions = {
        from: query,
        data: [],
      }

      try {
        if (suggestionStatus) {
          const semanticService = CONTAINER.get<SemanticService>(TYPES.SemanticService)
          const data = await semanticService.searchSingle(suggestionStatus.instance, {
            query,
            lang,
            limit: 10,
          })
          this.suggestions = {
            ...this.suggestions,
            data: this.getFilteredSuggestions(data),
          }
        }
      } catch (error) {
        this.$emit('showError', this.$epb.utils.decodeErrorMessage(error))
      } finally {
        this.loadingSuggestions = false
      }
    },
    selectValue(value: {
      comesFromInput: boolean
      query: string
      from?: string
      selected?: boolean
    }) {
      if (!value.comesFromInput) {
        return
      }
      this.clearSelectedValues(value.query)

      this.getSuggestions(value.query)
    },
    getFilteredSuggestions(suggestions: Array<{ query: string; distance: number }>) {
      return suggestions.filter(
        (element: { query: string; distance: number }) =>
          !this.item.synonyms.includes(element.query) &&
          !this.item.hyponyms.includes(element.query),
      )
    },
    removeValue(
      event,
      type: SynonymsTypes,
      value: { comesFromInput: boolean; query: string; from?: string },
    ) {
      this[type].splice(this[type].indexOf(value), 1)
      this.item[type].splice(this.item[type].indexOf(value.query), 1)

      if (!value.comesFromInput && this.suggestions.from === value.from) {
        this.suggestions.data.push(value)
        this.suggestions.data.sort(ArrayUtils.sortBy('distance'))
        return
      }
      if (value.comesFromInput && this.suggestions.from === value.query) {
        this.suggestions.data = []
        this.suggestions.from = ''
      }
    },
    onDraggableChange({ added, removed, moved }, type: SynonymsTypes) {
      if (added) {
        this.item[type] = [...this.item[type], added.element.query]
      }
      if (moved) {
        this.item[type].splice(moved.oldIndex, 1)
        this.item[type].splice(moved.newIndex, 0, moved.element.query)
      }
      if (removed) {
        this.item[type].splice(removed.oldIndex, 1)
      }
    },
  },
})
